import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
	fetchSiteAdminModelPrerequisiteCheck,
	fetchPageRoles,
} from '../../_api';
import { UserContext } from 'context/User/UserContext';
import { useField, useFormikContext } from 'formik';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import VerifiedIcon from '@mui/icons-material/Verified';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CustomFormControl from '../base/CustomFormControl';
import CustomHelperText from '../base/CustomHelperText';
import { NAVIGATION_ACTIONS } from 'pages/AnnualUserAccessReview/_const';
import { NEW_NON_EMPLOYEE_KEYS } from 'pages/NewNonEmployee/_const';

const PrerequisiteCheck = ({ onNavigate, ...props }) => {
	const [, meta, helpers] = useField(props);
	const { values } = useFormikContext();
	const { pathname } = useLocation();

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const [sCheckPermission, setCheckPermission] = useState(null);
	const [sCheckAccount, setCheckAccount] = useState(null);
	const [sError, setError] = useState('');

	useEffect(() => {
		let active = true;

		async function fetchApi() {
			const roles = (await fetchPageRoles(pathname)) ?? [];
			const canView = user?.Roles?.some(r =>
				roles.payload.includes(r.ID)
			);

			setCheckPermission(canView);
			const { payload, messages } =
				await fetchSiteAdminModelPrerequisiteCheck(
					user.ID,
					values[NEW_NON_EMPLOYEE_KEYS.SELF_SPONSOR] === 'true'
				);

			setCheckAccount(payload);
			helpers.setValue(payload);
			helpers.setTouched(true);

			if (!canView) {
				setError(
					'You do not have the proper roles and/or privileges to view this page.'
				);
			} else if (!payload && messages?.length > 0 && messages[0]?.Value) {
				setError(messages[0].Value);
			}
		}

		if (active && user.ID) {
			fetchApi();
		}

		return () => {
			active = false;
		};
	}, [user, helpers, values, pathname]);

	//watch for trigger to transition to next step and wait to allow user to see the message
	useEffect(() => {
		if (onNavigate && sCheckPermission === true && sCheckAccount === true) {
			const timer = setTimeout(() => {
				onNavigate(NAVIGATION_ACTIONS.NEXT);
			}, 2000);
			return () => clearTimeout(timer);
		}
	}, [sCheckPermission, sCheckAccount, onNavigate]);

	const LoadingAnimation = <CircularProgress size={20} />;
	const SuccessIcon = <CheckCircleOutlineIcon color='success' />;
	const ErrorIcon = <ErrorOutlineIcon color='error' />;
	return (
		<CustomFormControl>
			<Fade in={!sCheckPermission || !sCheckAccount}>
				<Box>
					<Stack direction={'column'} gap={1}>
						<Stack direction={'row'} gap={1} alignItems={'center'}>
							{sCheckPermission === null
								? LoadingAnimation
								: sCheckPermission
								? SuccessIcon
								: ErrorIcon}
							<Typography>Verifying permissions</Typography>
						</Stack>
						<Stack direction={'row'} gap={1} alignItems={'center'}>
							{sCheckAccount === null
								? LoadingAnimation
								: sCheckAccount
								? SuccessIcon
								: ErrorIcon}
							<Typography>Confirming account setup</Typography>
						</Stack>
					</Stack>
					<CustomHelperText
						error={meta.error}
						touched={meta.touched}
					/>
					<CustomHelperText error={sError} touched={true} />
				</Box>
			</Fade>
			<Box
				sx={{
					height: 180,
					width: '100%',
					display: 'flex',
					justifyContent: 'center',
				}}
			>
				<Fade in={sCheckPermission === true && sCheckAccount === true}>
					<Stack direction={'column'} gap={5} alignItems={'center'}>
						<VerifiedIcon
							color={'success'}
							sx={{ transform: 'scale(4)' }}
						/>
						<Typography variant={'h5'}>Verified</Typography>
					</Stack>
				</Fade>
			</Box>
		</CustomFormControl>
	);
};

export default PrerequisiteCheck;
